<!-- A prev/next arrow. `tabindex="-1"` keeps it clickable but out of the
     keyboard tab order — slide focus auto-centres the carousel, so dedicated
     keyboard-nav controls aren't needed. -->
<template>
  <button
    class="carousel__arrow click-passthrough-region"
    :class="[
      `carousel__arrow--${direction}`,
      {
        'carousel__arrow--hidden': hidden,
        'carousel__arrow--sprite': !!spriteProps,
        'carousel__arrow--inline': inline,
      },
    ]"
    tabindex="-1"
    :aria-label="direction === 'prev' ? 'Previous slide' : 'Next slide'"
  >
    <img
      v-if="spriteProps"
      v-bind="spriteProps.imgAttrs"
      alt=""
      class="carousel__arrow-sprite"
      :class="{ 'carousel__arrow-sprite--flipped': direction === 'next' }"
    />
    <span v-else class="carousel__arrow-icon">{{ direction === 'prev' ? '‹' : '›' }}</span>
  </button>
</template>

<script setup lang="ts">
import { useImagePreset, type ImageFieldData } from '../../../composables/useImagePreset'

const props = defineProps<{
  direction: 'prev' | 'next'
  hidden?: boolean
  inline?: boolean
  sprite?: ImageFieldData
}>()

const spriteProps = useImagePreset(() => props.sprite)
</script>

<style scoped>
.carousel__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--color-background-surface);
  box-shadow: var(--shadow-surface-raised);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    opacity 0.2s,
    background-color 0.2s,
    box-shadow 0.2s;
}

.carousel__arrow:hover {
  background: var(--color-background-surface-raised);
  box-shadow: var(--shadow-selectors-default);
}

.carousel__arrow--prev {
  inset-inline-start: 8px;
}

.carousel__arrow--next {
  inset-inline-end: 8px;
}

/* Below the slides: in the navigation row's flow instead of over the slides. */
.carousel__arrow--inline {
  position: static;
  transform: none;
  flex-shrink: 0;
}

.carousel__arrow--hidden {
  opacity: 0.3;
  pointer-events: none;
}

.carousel__arrow-icon {
  font-size: var(--text-body-lg-size);
  line-height: 1;
  color: var(--color-icon-default);
  user-select: none;
}

/* Arrows — sprite mode */

.carousel__arrow--sprite {
  background: transparent;
  box-shadow: none;
  border-radius: 0;
  width: auto;
  height: auto;
}

.carousel__arrow--sprite:hover {
  background: transparent;
  box-shadow: none;
}

.carousel__arrow-sprite {
  max-height: 36px;
  width: auto;
  opacity: 0.85;
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.carousel__arrow-sprite--flipped {
  transform: scaleX(-1);
}

/* Under RTL the prev/next arrows swap edges (inset-inline-*), so the chevron
   glyphs and sprites must point the opposite physical way to stay correct. */
[dir='rtl'] .carousel__arrow-icon {
  display: inline-block;
  transform: scaleX(-1);
}

[dir='rtl'] .carousel__arrow-sprite {
  transform: scaleX(-1);
}

[dir='rtl'] .carousel__arrow-sprite--flipped {
  transform: none;
}

.carousel__arrow--sprite:hover .carousel__arrow-sprite {
  opacity: 1;
}
</style>
